import { Box, Button, FormControl, FormLabel, MenuItem, Modal, Select, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import mysteryShopperService from "../../services/mysteryShopper";
import queryDefault from "../../utils/queryDefault";

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

const getYearOptions = () => {
  const thisYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, index) => thisYear - index);
};

const imageModalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
  maxHeight: "90vh",
  overflowY: "auto" as const,
};

interface ImagePreviewData {
  title: string;
  images: string[];
}

export default function ListMysteryShopperPage() {
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const [pageSize, setPageSize] = useState<number>(10);
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [imagePreviewData, setImagePreviewData] = useState<ImagePreviewData>({
    title: "",
    images: [],
  });
  const [options, setOptions] = useState<{
    month: number;
    year: number;
  }>({
    month: currentMonth,
    year: currentYear,
  });

  const filterForm = useForm({
    defaultValues: {
      month: currentMonth,
      year: currentYear,
    },
  });

  const listMysteryShoppers = useQuery(
    ["mystery-shopper-list", options],
    ({ queryKey }) => mysteryShopperService.getMysteryShopperList(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const onFilter = (values: any) => {
    setOptions({
      month: Number(values.month),
      year: Number(values.year),
    });
  };

  const openImagePreviewModal = (title: string, images: (string | null)[]) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    setImagePreviewData({
      title,
      images: images
        .filter((img): img is string => img !== null && img !== undefined)
        .map((img) => (img.startsWith("http") ? img : `${backendUrl}${img}`)),
    });
    setOpenImagePreview(true);
  };

  const rows = get(listMysteryShoppers, "data.data", []);

  return (
    <MainLayout>
      <Helmet>
        <title>Mystery Shopper</title>
      </Helmet>

      <Breadcrumb
        label="Mystery Shopper"
        breadcrumbs={[{ label: "Mystery Shopper", href: "/app/mystery-shopper" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[4, 5]}>
        <Box sx={{ mt: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <FormLabel>Month</FormLabel>
              <Controller
                name="month"
                control={filterForm.control}
                render={({ field }) => (
                  <Select {...field}>
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <FormLabel>Year</FormLabel>
              <Controller
                name="year"
                control={filterForm.control}
                render={({ field }) => (
                  <Select {...field}>
                    {getYearOptions().map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <Box sx={{ alignSelf: "end" }}>
              <Button variant="outlined" onClick={filterForm.handleSubmit(onFilter)}>
                Apply Filter
              </Button>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listMysteryShoppers.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={rows}
            rowCount={rows.length}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              { field: "shopper_name", headerName: "Shopper Name", minWidth: 150, flex: 1 },
              { field: "shopper_email", headerName: "Shopper Email", minWidth: 180, flex: 1 },
              { field: "visit_date", headerName: "Visit Date", minWidth: 120 },
              { field: "visit_time", headerName: "Visit Time", minWidth: 120 },
              { field: "division_id", headerName: "Division ID", minWidth: 100 },
              { field: "kebersihan_diri", headerName: "Kebersihan Diri", minWidth: 120, type: "number" },
              { field: "kerapihan_rambut", headerName: "Kerapihan Rambut", minWidth: 120, type: "number" },
              { field: "kerapihan_seragam", headerName: "Kerapihan Seragam", minWidth: 120, type: "number" },
              { field: "kebersihan_sepatu", headerName: "Kebersihan Sepatu", minWidth: 120, type: "number" },
              { field: "catatan_grooming", headerName: "Catatan Grooming", minWidth: 150, flex: 1 },
              { field: "crew_menyapa", headerName: "Crew Menyapa", minWidth: 120, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "keramahan", headerName: "Keramahan", minWidth: 100, type: "number" },
              { field: "komunikasi", headerName: "Komunikasi", minWidth: 100, type: "number" },
              { field: "responsiveness", headerName: "Responsiveness", minWidth: 120, type: "number" },
              { field: "fokus_customer", headerName: "Fokus Customer", minWidth: 120, type: "number" },
              { field: "attitude", headerName: "Attitude", minWidth: 100, type: "number" },
              { field: "catatan_behavior", headerName: "Catatan Behavior", minWidth: 150, flex: 1 },
              { field: "penyambutan", headerName: "Penyambutan", minWidth: 110, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "penjelasan", headerName: "Penjelasan", minWidth: 100, type: "number" },
              { field: "bantuan", headerName: "Bantuan", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "kejelasan_arahan", headerName: "Kejelasan Arahan", minWidth: 120, type: "number" },
              { field: "closing", headerName: "Closing", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_service_flow", headerName: "Catatan Service Flow", minWidth: 150, flex: 1 },
              { field: "pengetahuan_paket_harga", headerName: "Pengetahuan Paket Harga", minWidth: 150, type: "number" },
              { field: "menawarkan_loyalty_event", headerName: "Menawarkan Loyalty Event", minWidth: 150, type: "number" },
              { field: "menjelaskan_fitur", headerName: "Menjelaskan Fitur", minWidth: 120, type: "number" },
              { field: "menawarkan_additional_print", headerName: "Menawarkan Additional Print", minWidth: 150, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "kejelasan_informasi", headerName: "Kejelasan Informasi", minWidth: 120, type: "number" },
              { field: "catatan_product_knowledge", headerName: "Catatan Product Knowledge", minWidth: 150, flex: 1 },
              { field: "kebersihan_lantai", headerName: "Kebersihan Lantai", minWidth: 120, type: "number" },
              { field: "kebersihan_booth", headerName: "Kebersihan Booth", minWidth: 120, type: "number" },
              { field: "kebersihan_properti", headerName: "Kebersihan Properti", minWidth: 120, type: "number" },
              { field: "kenyamanan_tempat", headerName: "Kenyamanan Tempat", minWidth: 120, type: "number" },
              { field: "musik", headerName: "Musik", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_kondisi_store", headerName: "Catatan Kondisi Store", minWidth: 150, flex: 1 },
              { field: "mesin_normal", headerName: "Mesin Normal", minWidth: 110, type: "number" },
              { field: "kamera_jelas", headerName: "Kamera Jelas", minWidth: 110, type: "number" },
              { field: "printer_hasil", headerName: "Printer Hasil", minWidth: 110, type: "number" },
              { field: "error", headerName: "Error", minWidth: 90, renderCell: (params) => params.value ? "Ya" : "Tidak" },
              { field: "catatan_equipment", headerName: "Catatan Equipment", minWidth: 150, flex: 1 },
              { field: "penjelasan_flow_antrian", headerName: "Penjelasan Flow Antrian", minWidth: 150, type: "number" },
              { field: "waktu_tunggu", headerName: "Waktu Tunggu", minWidth: 110, type: "number" },
              { field: "kerapihan_antrian", headerName: "Kerapihan Antrian", minWidth: 120, type: "number" },
              { field: "pengaturan_flow", headerName: "Pengaturan Flow", minWidth: 120, type: "number" },
              { field: "catatan_antrian", headerName: "Catatan Antrian", minWidth: 150, flex: 1 },
              { field: "kepuasan_keseluruhan", headerName: "Kepuasan Keseluruhan", minWidth: 140, type: "number" },
              { field: "kembali_lagi", headerName: "Kembali Lagi", minWidth: 110, type: "number" },
              { field: "rekomendasi", headerName: "Rekomendasi", minWidth: 110, type: "number" },
              { field: "final_score", headerName: "Final Score", minWidth: 110, type: "number", renderCell: (params) => {
                const num = Number(params.value);
                return isNaN(num) ? 0 : Number(num.toFixed(2));
              } },
              { field: "red_flag", headerName: "Red Flag", minWidth: 120 },
              { field: "cerita_pengalaman", headerName: "Cerita Pengalaman", minWidth: 150, flex: 1 },
              {
                field: "foto_crew",
                headerName: "Foto Crew",
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                  const row = params.row;
                  const fotoCrewImages = [
                    row.foto_crew_1,
                    row.foto_crew_2,
                    row.foto_crew_3,
                    row.foto_crew_4,
                    row.foto_crew_5,
                  ].filter((img) => img);

                  return fotoCrewImages.length > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openImagePreviewModal(`Foto Crew - ${row.shopper_name}`, fotoCrewImages)}
                    >
                      Preview ({fotoCrewImages.length})
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                },
              },
              {
                field: "foto",
                headerName: "Foto Store",
                minWidth: 120,
                sortable: false,
                renderCell: (params) => {
                  const row = params.row;
                  const fotoImages = [
                    row.foto_1,
                    row.foto_2,
                    row.foto_3,
                    row.foto_4,
                    row.foto_5,
                    row.foto_6,
                    row.foto_7,
                    row.foto_8,
                    row.foto_9,
                    row.foto_10,
                  ].filter((img) => img);

                  return fotoImages.length > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openImagePreviewModal(`Foto Store - ${row.shopper_name}`, fotoImages)}
                    >
                      Preview ({fotoImages.length})
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                },
              },
              { field: "createdby", headerName: "Created By", minWidth: 120 },
              { field: "createdtime", headerName: "Created Time", minWidth: 160 },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>

      <Modal open={openImagePreview} onClose={() => setOpenImagePreview(false)}>
        <Box sx={imageModalStyle}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {imagePreviewData.title}
          </Typography>

          <Stack spacing={2}>
            {imagePreviewData.images.map((image, index) => (
              <Box key={index} sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Image {index + 1}
                </Typography>
                <img
                  src={image}
                  alt={`Preview ${index + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            ))}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => setOpenImagePreview(false)}>
              Close
            </Button>
          </Stack>
        </Box>
      </Modal>
    </MainLayout>
  );
}
